import React, { useContext, ReactElement, Context, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext, ThemeContextValues } from '../ThemeContext';

export enum Themes {
  LIGHT = 'light-theme',
  DARK = 'dark-theme',
  VAULT = 'vault-theme',
}

export type Props = {
  customContext?: Context<ThemeContextValues>;
  children?: ReactNode;
};

export function withTheme<TProps extends {}>(
  WrappedComponent: React.ComponentType<TProps>
): React.ComponentType<TProps & Props> {
  const WithTheme: React.ComponentType = ({
    customContext,
    ...props
  }: TProps & Props): ReactElement<TProps & Props> => {
    const { theme } = useContext(customContext || ThemeContext);
    return (
      <WrappedComponent
        themeClass={Themes[theme] || Themes.LIGHT}
        {...(props as TProps)}
      />
    );
  };

  WithTheme.propTypes = {
    customContext: PropTypes.shape({
      theme: PropTypes.string,
    }),
  };

  WithTheme.displayName = getDisplayName(WrappedComponent);
  return WithTheme;
}

export function getDisplayName({
  displayName,
  name,
}: {
  displayName?: string;
  name?: string;
}): string {
  return setThemedName(displayName || name) || 'ThemedComponent';
}

function setThemedName(name: string): string {
  if (!name) return null;
  return `Themed${name}`;
}
